/**
 * Pure helpers for the /welcome routes — kept separate from the React
 * components so they can be unit-tested without a DOM, matching the auth
 * app's existing logic-test style.
 */

const MIN_PASSWORD_LENGTH = 12;

/** Validation error keys understood by `welcome_translations`. */
export type PasswordError =
  | 'error_password_too_short'
  | 'error_password_mismatch'
  | 'error_password_missing_uppercase'
  | 'error_password_missing_lowercase'
  | 'error_password_missing_digit'
  | 'error_password_missing_special';

/**
 * Extracts the `token` query parameter from a location search string.
 *
 * @param search - e.g. `window.location.search` (`?token=…`).
 * @returns The token, or null when missing/empty.
 */
export function read_token(search: string): string | null {
  const token = new URLSearchParams(search).get('token') ?? '';
  return token !== '' ? token : null;
}

/**
 * Client-side password gate, mirroring the kernel's strictest character rules
 * (`password_strength = 0`): at least 12 characters with an upper-case letter,
 * a lower-case letter, a digit and a special character.
 *
 * The kernel stays authoritative — it additionally rejects dictionary words and
 * fragments of the username, and its level is configurable per installation, so
 * its 422 message is always shown verbatim when it refuses. This gate only
 * catches the mechanical rules before a round trip.
 *
 * @param password - The new password.
 * @param password_repeat - The confirmation field.
 * @returns A `PasswordError` key, or null when acceptable.
 */
export function validate_password(password: string, password_repeat: string): PasswordError | null {
  if (password.length < MIN_PASSWORD_LENGTH) return 'error_password_too_short';
  if (/[A-Z]/.test(password) === false) return 'error_password_missing_uppercase';
  if (/[a-z]/.test(password) === false) return 'error_password_missing_lowercase';
  if (/[0-9]/.test(password) === false) return 'error_password_missing_digit';
  if (/[^A-Za-z0-9]/.test(password) === false) return 'error_password_missing_special';
  if (password !== password_repeat) return 'error_password_mismatch';
  return null;
}
